import { useEffect, useRef, useState } from "react";
import { BookOpenCheck, MapPin, MessageSquareQuote, Sparkles } from "lucide-react";
import { AGENCY_TZ, api, formatDate } from "../api";
import { ErrorState, LoadingState, MetricCard } from "./Ui";
import { periodText } from "./PortalPerformance";
import "../polish/seo-report.css";

type Send = (path: string, body?: Record<string, unknown>, method?: string, signal?: AbortSignal) => Promise<any>;
const staffSend: Send = (path, body, method = "GET", signal) => api(path, { method, signal, ...(body ? { body: JSON.stringify(body) } : {}) });

type Keyword = { keyword: string; now: number | null; was: number | null; change: number | null; volume: number | null };
type Report = {
  month: string; from: string; to: string;
  playbook: { overall: number | null; parts: { name: string; done: number; tasks: number }[]; done_this_month: { id: string; part: string; action: string }[] } | null;
  rankings: { locations: { name: string | null; latest: string; previous: string | null; keywords: Keyword[]; up: number; down: number; top3: number }[] } | null;
  ai_search: { latest: { answers: number; named: number; share: number | null; at: string }; previous: { answers: number; named: number; share: number | null } | null } | null;
  reviews: { count: number; rating: number | null; prev_count: number; prev_rating: number | null; awaiting_reply: number } | null;
  posts: { total: number; by_channel: { channel: string; n: number }[] } | null;
  work: { tasks: { title: string; completed_at: string }[] } | null;
  search: { searches: number; clicks: number; impressions: number; near_page_one: number; pulled_at: string } | null;
};

const thisMonth = () => new Intl.DateTimeFormat("en-CA", { timeZone: AGENCY_TZ, year: "numeric", month: "2-digit" }).format(new Date()).slice(0, 7);
/** This month and the five before it, newest first. */
function recentMonths(n = 6) {
  const [y, m] = thisMonth().split("-").map(Number);
  return Array.from({ length: n }, (_, i) => { const d = new Date(Date.UTC(y, m - 1 - i, 1)); return d.toISOString().slice(0, 7); });
}
const monthLabel = (ym: string) => new Date(`${ym}-15T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const rankText = (n: number | null) => (n == null ? "—" : n > 20 ? "20+" : String(n));
const CHANNEL: Record<string, string> = { facebook: "Facebook", gbp: "Google Business", google_business: "Google Business", zernio: "Zernio" };
const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * The monthly SEO report for one client: playbook progress, local rankings and what
 * moved, AI search, reviews, posts that went out and the SEO work finished. Staff see
 * it on SEO plan > Report; clients see the same report in their portal.
 */
export default function SeoReport({ clientId, send = staffSend }: { clientId: number; send?: Send }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  // The portal makes a new send on every render; reading it through a ref loads the report once per month picked.
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    const ctl = new AbortController();
    setError("");
    sendRef.current(`/api/seo-report/${clientId}?month=${month}`, undefined, "GET", ctl.signal)
      .then((r: Report) => setData(r))
      .catch((e: unknown) => { if (!ctl.signal.aborted) setError(e instanceof Error ? e.message : "The SEO report could not load."); });
    return () => ctl.abort();
  }, [clientId, month, attempt]);

  if (error) return <ErrorState message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!data) return <LoadingState label="Loading the SEO report" />;

  const locations = data.rankings?.locations || [];
  const up = locations.reduce((a, l) => a + l.up, 0), down = locations.reduce((a, l) => a + l.down, 0);
  const tracked = locations.reduce((a, l) => a + l.keywords.length, 0);
  const ai = data.ai_search;
  const r = data.reviews;
  const doneItems = data.playbook?.done_this_month || [];
  const tasks = data.work?.tasks || [];

  return (
    <div className="seo-report">
      <div className="seo-report-bar">
        <p className="muted">{periodText(data.from, data.to)}</p>
        <label className="seo-report-month"><span>Month</span>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {recentMonths().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </label>
      </div>

      <div className="module-metrics seo-report-metrics">
        <MetricCard label="SEO playbook" icon={<BookOpenCheck aria-hidden="true" />}
          value={data.playbook?.overall == null ? "—" : data.playbook.overall > 0 && data.playbook.overall < 0.01 ? "Under 1%" : `${Math.round(data.playbook.overall * 100)}%`}
          detail={doneItems.length ? `${plural(doneItems.length, "item", "items")} done this month` : "Share of the playbook done so far"} />
        <MetricCard label="Local rankings" icon={<MapPin aria-hidden="true" />}
          value={tracked ? `${up} up, ${down} down` : "—"}
          detail={tracked ? `Of ${plural(tracked, "keyword", "keywords")}, since the last weekly check` : "Not tracked yet"} />
        <MetricCard label="AI search" icon={<Sparkles aria-hidden="true" />}
          value={ai ? `${ai.latest.named} of ${ai.latest.answers}` : "—"}
          detail={ai ? `AI answers that name you${ai.previous ? `, was ${ai.previous.named} of ${ai.previous.answers}` : ""}` : "Not checked yet"} />
        <MetricCard label="New reviews" icon={<MessageSquareQuote aria-hidden="true" />}
          value={r ? String(r.count) : "—"}
          detail={r ? (r.count ? `Average ${r.rating?.toFixed(1)} stars, ${r.prev_count} the month before` : `${r.prev_count} the month before`) : "No reviews connected"} />
      </div>

      <section className="panel">
        {/* The note explains the table, so an empty panel says only its one empty line (the tile reads "Not tracked yet"). */}
        <div className="panel-heading"><div><h2>Local rankings</h2>
          {locations.length > 0 && <p className="seo-report-note">Where you show on Google Maps for each search, from the latest weekly check. Ranks past 20 show as 20+.</p>}</div></div>
        {locations.length ? locations.map((l) => (
          <div key={`${l.name}-${l.latest}`} className="seo-report-location">
            {(locations.length > 1 || l.name) && <h3>{l.name || "Your location"} <small className="muted">checked {formatDate(l.latest)}{l.previous ? `, compared with ${formatDate(l.previous)}` : ""}</small></h3>}
            <div className="table-wrap">
              <table className="kw-table">
                <thead><tr><th>Search</th><th className="num">Rank</th><th className="num">Change</th><th className="num seo-report-vol">Searches / mo</th></tr></thead>
                <tbody>{[...l.keywords].sort((a, b) => (b.volume ?? -1) - (a.volume ?? -1) || (a.now ?? 99) - (b.now ?? 99)).map((k) => (
                  <tr key={k.keyword}>
                    <td data-label="Search">{k.keyword}</td>
                    <td className="number-cell" data-label="Rank">{rankText(k.now)}</td>
                    <td className="number-cell" data-label="Change">{k.change ? <span className={`kw-move ${k.change > 0 ? "up" : "down"}`}>{k.change > 0 ? "▲" : "▼"}{Math.abs(k.change)}</span> : "—"}</td>
                    <td className="number-cell seo-report-vol" data-label="Searches / mo">{k.volume != null ? k.volume.toLocaleString() : "—"}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )) : <p className="seo-report-empty">Rankings show here once a rank tracker is set up for your location.</p>}
      </section>

      <section className="panel">
        <div className="panel-heading"><div><h2>Work done in {monthLabel(data.month)}</h2></div></div>
        {tasks.length || doneItems.length || data.posts?.total ? (
          <dl className="seo-report-work">
            {data.posts && data.posts.total > 0 && <div><dt>Posts published</dt><dd>{data.posts.by_channel.map((c) => `${plural(c.n, "post", "posts")} on ${CHANNEL[c.channel] || c.channel}`).join(", ")}</dd></div>}
            {tasks.length > 0 && <div><dt>SEO tasks finished</dt><dd><ul>{tasks.map((t, i) => <li key={i}>{t.title} <span className="muted">{formatDate(t.completed_at)}</span></li>)}</ul></dd></div>}
            {doneItems.length > 0 && <div><dt>Playbook items done</dt><dd><ul>{doneItems.map((t) => <li key={t.id}>{t.action} <span className="muted">{t.part}</span></li>)}</ul></dd></div>}
          </dl>
        ) : <p className="seo-report-empty">Nothing finished yet this month. Work from the weekly SEO plan shows here as it is done.</p>}
      </section>

      {(data.playbook?.parts.length || 0) > 0 && (
        <section className="panel">
          <div className="panel-heading"><div><h2>Playbook progress</h2><p className="seo-report-note">Items done so far in each area of the SEO playbook.</p></div></div>
          <div className="table-wrap">
            <table className="kw-table">
              <thead><tr><th>Area</th><th className="num">Done</th><th className="num">Items</th></tr></thead>
              <tbody>{data.playbook!.parts.map((p) => <tr key={p.name}><td data-label="Area">{p.name}</td><td className="number-cell" data-label="Done">{p.done}</td><td className="number-cell" data-label="Items">{p.tasks}</td></tr>)}</tbody>
            </table>
          </div>
        </section>
      )}

      {(data.search || (r && r.awaiting_reply > 0)) && (
        <section className="panel">
          <div className="panel-heading"><div><h2>Also worth knowing</h2></div></div>
          <dl className="seo-report-work">
            {data.search && <div><dt>Google search, last 28 days</dt><dd>{plural(data.search.clicks, "click", "clicks")} from {plural(data.search.impressions, "showing", "showings")}. {plural(data.search.near_page_one, "search sits", "searches sit")} on page 2 or 3, close to page one.</dd></div>}
            {r && r.awaiting_reply > 0 && <div><dt>Reviews waiting for a reply</dt><dd>{r.awaiting_reply}</dd></div>}
          </dl>
        </section>
      )}
    </div>
  );
}
